import type { CategoriesApi } from '@/v2/api/metadata/categories'
import type { SportsApi } from '@/v2/api/metadata/sports'
import type { TournamentRoundsApi } from '@/v2/api/metadata/tournament-rounds'
import type { TournamentsApi } from '@/v2/api/metadata/tournaments'
import type { DialogConfig } from '@/v2/framework/dialog/types'
import { buildAddSupplementalNamesPayload } from '@/v2/kits/supplemental-names'
import {
  displayIdRowFields,
  tournamentDefaultEventSelectionOptions,
  tournamentGamesStatusOptions,
  tournamentStatusOptions,
} from '../../shared'

export function createAddDialog(options: {
  roundsApi: TournamentRoundsApi
  sportsApi: SportsApi
  categoriesApi: CategoriesApi
  tournamentsApi: TournamentsApi
}): DialogConfig {
  const { roundsApi, sportsApi, categoriesApi, tournamentsApi } = options

  return {
    id: 'add-tournament-round',
    title: 'Add tournament round',
    trigger: { type: 'fab' },
    layout: { type: 'popover' },
    structure: {
      type: 'stepper',
      steps: [
        {
          id: 'main',
          children: [
            {
              type: 'group',
              name: 'main',
              label: 'Main',
              children: [
                {
                  type: 'select-field',
                  name: 'status',
                  label: 'Status',
                  required: true,
                  placeholder: 'Select status',
                  options: tournamentStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: 'select-field',
                  name: 'sportId',
                  label: 'Sport',
                  required: true,
                  placeholder: 'Select sport',
                  searchable: true,
                  options: async () => {
                    const sports = await sportsApi.getFilters({ page: 1, size: -1 })
                    return sports.map((sport) => ({
                      value: sport.id,
                      title: String(sport.name ?? ''),
                    }))
                  },
                  defaultValue: null,
                },
                {
                  type: 'select-field',
                  name: 'categoryId',
                  label: 'Category',
                  required: true,
                  placeholder: 'Select category',
                  searchable: true,
                  options: async ({ values }) => {
                    const sportId = (values.main as Record<string, unknown> | undefined)?.sportId
                    if (sportId == null || sportId === '') return []
                    const categories = await categoriesApi.getFilters({
                      page: 1,
                      size: -1,
                      sportId,
                    })
                    return categories.map((category) => ({
                      value: category.id,
                      title: String(category.name ?? ''),
                    }))
                  },
                  defaultValue: null,
                },
                {
                  type: 'select-field',
                  name: 'tournamentId',
                  label: 'Tournament',
                  required: true,
                  placeholder: 'Select tournament',
                  searchable: true,
                  options: async ({ values }) => {
                    const main = values.main as Record<string, unknown> | undefined
                    const categoryId = main?.categoryId
                    if (categoryId == null || categoryId === '') return []
                    const tournaments = await tournamentsApi.getFilters({
                      page: 1,
                      size: -1,
                      categoryId,
                    })
                    return tournaments.map((tournament) => ({
                      value: tournament.id,
                      title: String(tournament.name ?? ''),
                    }))
                  },
                  defaultValue: null,
                },
                {
                  type: 'text-field',
                  name: 'name',
                  label: 'Name',
                  required: true,
                  placeholder: 'Enter a name',
                },
                {
                  type: 'text-field',
                  name: 'info',
                  label: 'Info',
                  placeholder: 'Enter info',
                },
                {
                  type: 'component',
                  component: () =>
                    import('@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue'),
                  props: { mode: 'add', entity: 'TOURNAMENT_ROUND' },
                },
              ],
            },
            {
              type: 'group',
              name: 'prematchGame',
              label: 'Pre-match',
              children: [
                {
                  type: 'select-field',
                  name: 'status',
                  label: 'Status',
                  placeholder: 'Select pre-match status',
                  required: true,
                  options: tournamentGamesStatusOptions,
                },
                {
                  type: 'text-field',
                  name: 'position',
                  label: 'Position',
                  placeholder: 'Enter position',
                },
                {
                  type: 'structured-array-field',
                  name: 'displayIds',
                  label: 'Display IDs',
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdRowFields,
                },
                {
                  type: 'select-field',
                  name: 'defaultEventSelection',
                  label: 'Default Event Selection',
                  placeholder: 'Select default event selection for pre-match game',
                  required: true,
                  options: tournamentDefaultEventSelectionOptions,
                },
              ],
            },
            {
              type: 'group',
              name: 'liveGame',
              label: 'Live',
              children: [
                {
                  type: 'select-field',
                  name: 'status',
                  label: 'Status',
                  required: true,
                  options: tournamentGamesStatusOptions,
                  placeholder: 'Select live status',
                },
                {
                  type: 'text-field',
                  name: 'position',
                  label: 'Position',
                  placeholder: 'Enter position',
                },
                {
                  type: 'structured-array-field',
                  name: 'displayIds',
                  label: 'Display IDs',
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdRowFields,
                },
                {
                  type: 'select-field',
                  name: 'defaultEventSelection',
                  label: 'Default Event Selection',
                  placeholder: 'Select default event selection for live game',
                  required: true,
                  options: tournamentDefaultEventSelectionOptions,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>
        return roundsApi.create({
          status: main.status,
          sportId: main.sportId,
          categoryId: main.categoryId,
          tournamentId: main.tournamentId,
          name: main.name,
          info: main.info,
          supplementalNames: buildAddSupplementalNamesPayload(data, 'TOURNAMENT_ROUND'),
          prematchGame: {
            status: prematchGame.status,
            position: prematchGame.position,
            displayIds: prematchGame.displayIds,
            defaultEventSelection: prematchGame.defaultEventSelection,
          },
          liveGame: {
            status: liveGame.status,
            position: liveGame.position,
            displayIds: liveGame.displayIds,
            defaultEventSelection: liveGame.defaultEventSelection,
          },
        })
      },
    },
  }
}
